import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Info } from '@sorare/core/src/atoms/icons/Info';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { DisplayS, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

const StyledBezel = styled(Bezel)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(var(--unit) * 25);

  @media ${laptopAndAbove} {
    height: calc(var(--unit) * 25);
    width: calc(var(--unit) * 45);
    overflow: hidden;
  }
`;

const InfoContainer = styled(Bezel)`
  max-height: 90%;
  max-width: 90%;
  overflow-y: auto;
  position: absolute;
  z-index: 1;
  background-color: var(--c-black);
  border: 1px solid var(--c-white);
  border-radius: var(--unit);

  ul {
    padding-left: var(--double-unit);
  }

  li::before {
    content: '•';
    color: var(--c-white);
    display: inline-block;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: var(--double-unit);
  right: var(--half-unit);
  cursor: pointer;
`;

const TextContainer = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--unit);
`;

const InnerWrapper = styled(Vertical).attrs({ gap: 1, center: true })`
  padding: var(--double-unit);
`;

export const ProfessionalEqualityIndex = () => {
  const [showInfoBox, toggleInfoBox] = useToggle(false);
  return (
    <StyledBezel bezelSize={20}>
      <InnerWrapper>
        <LabelL bold>Professional Equality Index</LabelL>
        <Horizontal center>
          <DisplayS bold>78 / 100</DisplayS>
          <Tooltip title="For 2025 based on 2024 numbers">
            <Info />
          </Tooltip>
        </Horizontal>
        <Button color="transparent" size="small" onClick={toggleInfoBox}>
          Learn more
        </Button>
        {!!showInfoBox && (
          <InfoContainer onClick={toggleInfoBox}>
            <CloseButton
              size="medium"
              onClick={toggleInfoBox}
              icon={iconX}
              color="tertiary"
            />
            <TextContainer>
              <LabelL bold>Professional equality index</LabelL>
              <LabelS>
                This index measures gender wage disparities in French companies
                with more than 50 employees, based on 5 indicators:
                <ul>
                  <li>Pay gaps between women and men</li>
                  <li>Pay gaps in individual raises</li>
                  <li>
                    The number of employees who were given a raise after their
                    maternity leave
                  </li>
                  <li>
                    Equality between women and men among the 10 highest wages
                  </li>
                  <li>
                    Promotion rate gaps (for companies with more than 250
                    employees).
                  </li>
                </ul>
                If one indicator cannot be measured, the Index is not
                applicable.
              </LabelS>
            </TextContainer>
          </InfoContainer>
        )}
      </InnerWrapper>
    </StyledBezel>
  );
};
